import { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useStickyToolbarRegister } from "@/hooks/useStickyToolbar";

interface BulkActionBarProps {
  count: number;
  onClear: () => void;
  entityLabel?: string;
  children?: ReactNode;
  className?: string;
  /**
   * AUT-419: pin the bar to the top of the viewport, stacked above the sticky table header. Requires
   * a StickyToolbarProvider around the bar + table.
   */
  sticky?: boolean;
  /** Render the bar even when `count` is 0 (e.g. a list in select mode with nothing picked yet). */
  forceVisible?: boolean;
  /** Extra actions grouped on the right, after `Clear selection`. */
  rightActions?: ReactNode;
  /** Put the right group on its own full-width line below the label. */
  stackRightActions?: boolean;
}

export function BulkActionBar({
  count,
  onClear,
  entityLabel,
  children,
  className,
  sticky = false,
  forceVisible = false,
  rightActions,
  stackRightActions = false,
}: BulkActionBarProps) {
  const register = useStickyToolbarRegister();
  if (count <= 0 && !forceVisible) return null;

  const label = entityLabel
    ? `${count} ${entityLabel}${count !== 1 ? "s" : ""} selected`
    : `${count} selected`;

  const clearButton = (
    <Button
      variant="ghost"
      size="sm"
      className={cn("text-xs h-7 text-primary", rightActions === undefined && "ml-auto")}
      onClick={onClear}
    >
      Clear selection
    </Button>
  );

  const bar = (
    <div
      className={cn(
        "flex items-center gap-3 px-3 py-2 bg-primary-tint-soft rounded-md animate-fade-in",
        rightActions !== undefined && stackRightActions && "flex-wrap",
        className,
      )}
    >
      {children}
      <span className="text-xs font-normal text-foreground">{label}</span>
      {rightActions !== undefined ? (
        <div
          className={cn(
            "flex items-center gap-1",
            stackRightActions ? "w-full justify-start -ml-2" : "ml-auto",
          )}
        >
          {count > 0 && clearButton}
          {rightActions}
        </div>
      ) : (
        count > 0 && clearButton
      )}
    </div>
  );

  if (!sticky) return bar;

  // AUT-419: pinned, matching the prototype — opaque card wrapper + top/bottom bleed masks.
  return (
    <div
      ref={register}
      className="sticky top-0 z-40 bg-card py-1 relative before:absolute before:inset-x-0 before:bottom-full before:h-2 before:bg-card before:content-[''] after:absolute after:inset-x-0 after:top-full after:h-2 after:bg-card after:content-['']"
    >
      {bar}
    </div>
  );
}
